/**
 * The nth-level explorer.
 *
 * There is no fixed L1 → L2 → L3 chain. A path is an ordered list of
 * dim:value steps; every dimension not already on the path can be opened at any
 * level through the "break down by" picker; and the chain ends on one invoice,
 * then one of its stage transitions — a single audited fact.
 *
 * Every tile and every chart element is a drill target, because a number nobody
 * can open is a number nobody can check.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area, AreaChart, Bar as RBar, BarChart, CartesianGrid, Cell, Legend, Line,
  Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import { Bookmark, ChevronRight, CornerDownRight, Layers, Loader2, Receipt, Users }
  from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PARTY, STAGE_LABEL, day, int, money, num, stamp, toneColor, useIptPrefs, useIptTheme }
  from './iptTheme';
import { Bar, Def, Empty, Panel, Sample, StageChip, Tile, WaitingPill } from './IptPrimitives';
import { CognaraPanel } from './CognaraPanel';
import { VendorMonthKpi } from './VendorMonthKpi';

const enc = (v) => String(v).replace(/\|/g, '~p').replace(/:/g, '~c');
const push = (path, dim, value) => (path ? `${path}|` : '') + `${dim}:${enc(value)}`;

export const IptExplorer = ({ projectId }) => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const navigate = useNavigate();
  const [basis, setBasis] = useState('receipt');
  const [path, setPath] = useState('');
  const [by, setBy] = useState(null);
  const [sort, setSort] = useState('value');
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState([]);
  const [invoiceNode, setInvoiceNode] = useState(null);
  const [trNode, setTrNode] = useState(null);

  const qs = useMemo(() => {
    const sp = new URLSearchParams();
    if (projectId) sp.set('project_id', projectId);
    sp.set('month_basis', basis);
    sp.set('day_basis', prefs.dayBasis);
    sp.set('stop_clock', String(prefs.stopClock));
    return sp;
  }, [projectId, basis, prefs.dayBasis, prefs.stopClock]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams(qs);
      if (path) sp.set('path', path);
      if (by) sp.set('by', by);
      if (sort !== 'value') sp.set('sort', sort);
      const r = await api.get(`/ipt/analytics/explore?${sp}`);
      setNode(r.data);
    } catch { setNode(null); } finally { setLoading(false); }
  }, [qs, path, by, sort]);
  useEffect(() => { load(); }, [load]);

  const loadSaved = useCallback(async () => {
    try {
      const r = await api.get('/ipt/saved-views?kind=drill_path');
      setSaved(r.data?.rows || []);
    } catch { setSaved([]); }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  /** A drill can filter, choose the next breakdown, or both — a ratio tile opens
   * its own denominator AND the yes/no dimension that produced the number. */
  const drill = useCallback((dim, value, nextBy) => {
    if (dim && (value === null || value === undefined || value === '')) return;
    if (dim) setPath((p) => push(p, dim, value));
    // a net cycle band has nothing left to group by — it opens the ranked list
    const ranked = dim === 'cycle_band';
    setSort(ranked ? 'cycle' : 'value');
    setBy(ranked ? 'invoice' : (nextBy || null));
    setInvoiceNode(null);
    setTrNode(null);
  }, []);

  const goTo = useCallback((p) => {
    setPath(p || '');
    setBy(null);
    setSort('value');
    setInvoiceNode(null);
    setTrNode(null);
  }, []);

  const openInvoice = useCallback(async (invoiceId) => {
    setTrNode(null);
    setInvoiceNode({ loading: true });
    try {
      const sp = new URLSearchParams();
      if (projectId) sp.set('project_id', projectId);
      const r = await api.get(`/ipt/analytics/explore/invoice/${invoiceId}?${sp}`);
      setInvoiceNode(r.data);
    } catch { setInvoiceNode(null); }
  }, [projectId]);

  const openTransition = useCallback(async (transitionId) => {
    if (!transitionId) return;
    setTrNode({ loading: true });
    try {
      const sp = new URLSearchParams();
      if (projectId) sp.set('project_id', projectId);
      const r = await api.get(`/ipt/analytics/explore/transition/${transitionId}?${sp}`);
      setTrNode(r.data);
    } catch { setTrNode(null); }
  }, [projectId]);

  if (loading && !node) {
    return (
      <div className="py-20 text-center" data-testid="ipt-analytics-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
      </div>
    );
  }
  if (!node) return <Empty T={T} testId="ipt-analytics-error">Analytics could not be loaded.</Empty>;

  const k = node.kpi;
  const cur = k.volume.currency;

  if (!k.volume.invoices && !node.depth) {
    return (
      <Panel T={T} testId="ipt-analytics-empty" title="No invoices on this project"
        subtitle="Nothing to analyse — an empty register, not a failed load">
        <p className="text-[11.5px] leading-relaxed max-w-[640px]" style={{ color: T.body }}>
          The invoice module holds no records for the currently selected project, so every
          KPI here would be a dash. Switch project in the header, or import from Contracts
          &amp; Claims. Cognara is not offered on an empty population — there is nothing for
          it to read.
        </p>
      </Panel>
    );
  }

  return (
    <div className="space-y-4" data-testid="ipt-analytics">
      <Breadcrumb node={node} T={T} onGo={goTo} basis={basis} setBasis={setBasis} k={k} />

      <SavedPaths T={T} saved={saved} node={node} basis={basis} projectId={projectId}
        onOpen={(v) => { setPath(v.path || ''); setBy(v.breakdown_by || null);
          setSort('value'); setInvoiceNode(null); setTrNode(null); }}
        onChanged={loadSaved} />

      <KpiStrip k={k} cur={cur} T={T} onDrill={drill} node={node} />
      <Sample T={T} testId="an-sample" text={`${k.sample.statement} ${k.volume.overlapNote}`} />

      <CognaraPanel level={node.depth ? 'NODE' : 'L1'} projectId={projectId}
        monthBasis={basis} path={path || undefined}
        subtitle={node.depth
          ? node.breadcrumb.slice(1).map((b) => `${b.dimLabel}: ${b.label}`).join(' › ')
          : 'The whole population in scope'} />

      <Charts node={node} k={k} cur={cur} T={T} basis={basis} onDrill={drill} />

      {!node.depth && (
        <VendorMonthKpi projectId={projectId} basis={basis}
          onDrill={(vendor, month) => {
            let p = push('', 'vendor', vendor);
            if (month) p = push(p, 'month', month);
            goTo(p);
          }} />
      )}

      <BreakdownPicker node={node} T={T}
        setBy={(d) => { setSort('value'); setBy(d); }} />

      {node.mode === 'groups' ? (
        <GroupTable node={node} cur={cur} T={T} onDrill={drill} />
      ) : (
        <InvoiceTable node={node} cur={cur} T={T} onOpen={openInvoice}
          onFull={(id) => navigate(`/project-controls/cost/invoices/${id}?tab=lifecycle`)} />
      )}

      {invoiceNode && (
        <InvoicePanel d={invoiceNode} T={T} cur={cur} onOpenTransition={openTransition}
          onClose={() => { setInvoiceNode(null); setTrNode(null); }}
          onFull={(id) => navigate(`/project-controls/cost/invoices/${id}?tab=lifecycle`)}
          projectId={projectId} basis={basis} />
      )}

      {trNode && (
        <TransitionPanel d={trNode} T={T} onClose={() => setTrNode(null)}
          onStep={openTransition} projectId={projectId} basis={basis} />
      )}
    </div>
  );
};


/* ── saved drill paths ────────────────────────────────────────────────── */
const SavedPaths = ({ T, saved, node, basis, projectId, onOpen, onChanged }) => {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const save = async () => {
    const label = name.trim() || (node.breadcrumb.slice(1)
      .map((b) => `${b.dimLabel}: ${b.label}`).join(' › ') || 'Whole population');
    try {
      await api.post('/ipt/saved-views', {
        kind: 'drill_path', name: label, path: node.path || null,
        project_id: projectId, breakdown_by: node.breakdownBy,
        month_basis: basis,
        crumbs: node.breadcrumb.slice(1).map((b) => ({ dim: b.dim, value: b.value,
          dimLabel: b.dimLabel, label: b.label })),
      });
      toast.success('Path saved — it reopens on the numbers as they stand then');
      setNaming(false); setName(''); onChanged();
    } catch { toast.error('Could not save this path'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/ipt/saved-views/${id}`); onChanged(); } catch { /* noop */ }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="an-saved-paths">
      <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.09em]"
        style={{ color: T.muted }}>
        <Bookmark className="w-3 h-3" /> saved paths
      </span>
      {saved.map((v) => (
        <span key={v.view_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
          data-testid={`an-saved-${v.view_id}`}
          style={{ border: `1px solid ${T.border}` }}>
          <button type="button" onClick={() => onOpen(v)} className="text-[10px] font-semibold"
            style={{ color: T.primary }} title={v.path || 'whole population'}>
            {v.name}
          </button>
          <button type="button" onClick={() => remove(v.view_id)}
            data-testid={`an-saved-del-${v.view_id}`} className="text-[10px]"
            style={{ color: T.muted }}>×</button>
        </span>
      ))}
      {!saved.length && (
        <span className="text-[10px]" style={{ color: T.muted }}>
          none yet — save where you are and reopen it next week on fresh numbers
        </span>
      )}
      {naming ? (
        <span className="inline-flex items-center gap-1">
          <input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="an-save-name" placeholder="name this path"
            className="text-[10.5px] rounded px-2 py-0.5 outline-none"
            style={{ background: T.surfaceAlt, color: T.ink,
                     border: `1px solid ${T.border}`, width: 190 }} />
          <button type="button" onClick={save} data-testid="an-save-confirm"
            className="px-2 py-0.5 rounded text-[10px] font-semibold"
            style={{ background: T.primary, color: '#fff' }}>Save</button>
          <button type="button" onClick={() => setNaming(false)} data-testid="an-save-cancel"
            className="text-[10px]" style={{ color: T.muted }}>cancel</button>
        </span>
      ) : (
        <button type="button" onClick={() => setNaming(true)} data-testid="an-save-path"
          className="px-2 py-0.5 rounded text-[10px] font-semibold"
          style={{ color: T.body, border: `1px solid ${T.border}` }}>
          + save this path
        </button>
      )}
      <span className="text-[9.5px]" style={{ color: T.muted }} data-testid="an-saved-note">
        the path is stored, never the figures it produced
      </span>
      <a href="/project-controls/cost/invoices?tab=vendors" data-testid="an-vendor-console-link"
        className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
        style={{ color: T.primary, border: `1px solid ${T.tint(T.primary, '44')}` }}>
        <Users className="w-3 h-3" /> Vendor console
      </a>
    </div>
  );
};

/* ── breadcrumb ───────────────────────────────────────────────────────── */
const Breadcrumb = ({ node, T, onGo, basis, setBasis, k }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid="an-breadcrumb">
    {node.breadcrumb.map((b, i) => (
      <span key={b.path || 'root'} className="inline-flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="w-3 h-3" style={{ color: T.muted }} />}
        <button type="button" onClick={() => onGo(b.path)}
          data-testid={`an-crumb-${i}`}
          className="px-2 py-0.5 rounded text-[10.5px] font-semibold"
          style={i === node.breadcrumb.length - 1
            ? { background: T.tint(T.primary, '1A'), color: T.primary }
            : { color: T.muted, border: `1px solid ${T.border}` }}>
          {i === 0 ? `L1 · whole population` : `L${i + 1} · ${b.dimLabel}: ${b.label}`}
          <span className="ml-1 text-[9px] font-normal">{int(b.invoices)}</span>
        </button>
      </span>
    ))}
    <div className="ml-auto flex items-center gap-2">
      <span className="text-[9.5px] uppercase tracking-[0.09em]" style={{ color: T.muted }}>
        month basis
      </span>
      {(node.monthBasisOptions || []).map((o) => (
        <button key={o.value} type="button" onClick={() => setBasis(o.value)}
          data-testid={`ipt-month-basis-${o.value}`}
          className="px-2.5 py-1 rounded-full text-[10.5px] font-semibold"
          style={basis === o.value
            ? { background: T.tint(T.primary, '1F'), color: T.primary,
                border: `1px solid ${T.tint(T.primary, '55')}` }
            : { color: T.muted, border: `1px solid ${T.border}` }}>
          {o.label}
        </button>
      ))}
      <Def T={T} testId="ipt-month-basis-def" title="Month basis"
        body={(node.monthBasisOptions || []).map((o) => `${o.label}: ${o.note}`).join(' ')}
        basis={k.basis} />
    </div>
  </div>
);

/* ── the KPI strip, every tile a drill target ─────────────────────────── */
const KpiStrip = ({ k, cur, T, onDrill, node }) => {
  const tile = (props) => <Tile T={T} {...props} />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
      {tile({ testId: 'an-tile-volume', label: 'Invoices in scope',
        value: int(k.volume.invoices),
        sub: `${money(k.volume.grossValue, cur)} gross · ${money(k.volume.netValue, cur)} net`,
        onClick: () => onDrill(null, null, 'invoice'),
        def: { title: 'Invoices in scope', body: k.volume.definition, basis: k.basis,
          sample: `${k.band.note} Opens every invoice in this node.` } })}
      {tile({ testId: 'an-tile-fpy', label: 'First-pass yield',
        value: k.firstPassYield.value === null ? '—' : `${num(k.firstPassYield.value)}%`,
        tone: k.firstPassYield.value >= 85 ? T.green
          : (k.firstPassYield.value >= 70 ? T.amber : T.red),
        sub: `${int(k.firstPassYield.clean)} clean of ${int(k.volume.invoices)}`,
        onClick: () => onDrill(null, null, 'first_pass'),
        def: { title: 'First-pass yield', body: k.firstPassYield.definition, basis: k.basis,
          sample: 'Opens the whole denominator split clean vs touched, so both sides are '
            + 'visible and you can filter on from either.' } })}
      {tile({ testId: 'an-tile-stp', label: 'Straight-through',
        value: k.straightThrough.value === null ? '—' : `${num(k.straightThrough.value)}%`,
        sub: `${int(k.straightThrough.n)} of ${int(k.straightThrough.of)} paid`,
        onClick: () => onDrill('paid', 'yes', 'straight_through'),
        def: { title: 'Straight-through processing', body: k.straightThrough.definition,
          basis: k.basis,
          sample: 'Opens the denominator — paid invoices only — split straight-through '
            + 'vs not. Invoices still in flight are excluded, not counted as failures.' } })}
      {tile({ testId: 'an-tile-otp', label: 'On-time payment',
        value: k.onTimePayment.value === null ? '—' : `${num(k.onTimePayment.value)}%`,
        tone: k.onTimePayment.value >= 90 ? T.green : T.amber,
        sub: `${int(k.onTimePayment.n)} of ${int(k.onTimePayment.of)} paid`,
        onClick: () => onDrill('paid', 'yes', 'paid_on_time'),
        def: { title: 'On-time payment', body: k.onTimePayment.definition, basis: k.basis,
          sample: 'Opens the paid denominator split on-time vs late, with paid invoices '
            + 'missing a release or due date as their own group — unknown, not late.' } })}
      {tile({ testId: 'an-tile-cycle', label: 'Net cycle (excl. imported)',
        value: k.cycleTime.netExclImported.mean === null ? '—'
          : `${num(k.cycleTime.netExclImported.mean)} d`,
        sub: `median ${num(k.cycleTime.netExclImported.median)} · n=${int(k.cycleTime.netExclImported.n)}`
          + ` · all-in ${num(k.cycleTime.net.mean)} d`,
        onClick: () => onDrill('paid', 'yes', 'cycle_band'),
        def: { title: 'Net cycle time', body: k.cycleTime.definition, basis: k.basis,
          sample: `${int(k.cycleTime.importedInMean)} imported records sit inside the all-in `
            + 'mean; their span is an artefact of the import date. A mean has no '
            + 'population, so this opens paid invoices banded by net cycle — and a band '
            + 'opens the slowest-first list.' } })}
      {tile({ testId: 'an-tile-dispute', label: 'Dispute rate',
        value: k.disputes.rate === null ? '—' : `${num(k.disputes.rate)}%`,
        tone: k.disputes.rate > 10 ? T.red : T.amber,
        sub: `${int(k.disputes.everCount)} ever · ${int(k.disputes.openCount)} open · `
          + `${num(k.disputes.resolutionRate)}% resolved`,
        onClick: () => onDrill('stage', 'DISPUTED'),
        def: { title: 'Dispute rate', body: k.disputes.definition, basis: k.basis,
          sample: 'Opens the invoices currently in dispute, which you can then break '
            + 'down by any remaining dimension.' } })}
      {tile({ testId: 'an-tile-rework', label: 'Rework loops / invoice',
        value: k.reworkLoops.value === null ? '—' : num(k.reworkLoops.value, 2),
        sub: `worst invoice: ${int(k.reworkLoops.max)} excursions`,
        onClick: () => onDrill(null, null, 'rework'),
        def: { title: 'Rework loops', body: k.reworkLoops.definition,
          sample: 'Opens the population split by excursion count — 0, 1, 2, 3+.' } })}
      {tile({ testId: 'an-tile-leakage', label: 'Deductions applied',
        value: k.deductionLeakage.pctOfGross === null ? '—'
          : `${num(k.deductionLeakage.pctOfGross, 2)}%`,
        sub: `${money(k.deductionLeakage.applied, cur)} of gross · `
          + `${money(k.deductionLeakage.waived, cur)} waived`,
        onClick: () => onDrill(null, null, 'deduction_type'),
        def: { title: 'Deduction leakage', body: k.deductionLeakage.definition,
          sample: 'Opens a type-by-type view. An invoice carrying three types appears '
            + 'under all three, so those rows double-count — the table says so above '
            + 'itself and the counts must not be added.' } })}
      {tile({ testId: 'an-tile-blocked', label: 'Blocked', tone: T.amber,
        value: money(k.blocked.value, cur),
        sub: `${int(k.blocked.count)} invoices · ${num(k.blocked.pctOfUnpaidValue, 1)}% of unpaid`,
        onClick: () => onDrill('blocked', 'yes'),
        def: { title: 'Blocked', body: k.blocked.definition, sample: k.volume.overlapNote } })}
      {tile({ testId: 'an-tile-overdue', label: 'Overdue', tone: T.red,
        value: money(k.overdue.value, cur),
        sub: `${int(k.overdue.count)} invoices · worst ${num(k.overdue.worstDays, 0)} d`,
        onClick: () => onDrill('overdue', 'yes'),
        def: { title: 'Overdue', body: k.overdue.definition, sample: k.volume.overlapNote } })}
      {tile({ testId: 'an-tile-bottleneck', label: 'Slowest controlled stage',
        value: k.bottleneck.worst ? `${num(k.bottleneck.worst.avgDaysExclImported)} d` : '—',
        sub: k.bottleneck.worst
          ? `${k.bottleneck.worst.label} · n=${int(k.bottleneck.worst.nNative)} native` : '—',
        onClick: k.bottleneck.worst
          ? () => onDrill('stage', k.bottleneck.worst.stage) : undefined,
        def: { title: 'Slowest controlled stage', body: k.bottleneck.definition,
          basis: k.basis } })}
      {tile({ testId: 'an-tile-imported', label: 'Imported in these figures',
        value: `${num(k.band.pctImported, 1)}%`, tone: T.amber,
        sub: `${int(k.band.imported)} of ${int(k.band.total)} · ${money(k.band.importedValue, cur)}`,
        onClick: () => onDrill('imported', 'yes'),
        def: { title: 'Imported band', body: k.band.note } })}
    </div>
  );
};

/* ── the "break down by" picker ───────────────────────────────────────── */
const BreakdownPicker = ({ node, T, setBy }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid="an-breakdown-picker">
    <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.09em]"
      style={{ color: T.muted }}>
      <Layers className="w-3 h-3" /> break down by
    </span>
    {(node.breakdownOptions || []).map((o) => (
      <button key={o.dim} type="button" onClick={() => setBy(o.dim)}
        data-testid={`an-by-${o.dim}`} disabled={!o.splits}
        title={o.splits ? o.note : `${o.note} — does not split this population further`}
        className="px-2 py-0.5 rounded text-[10px] font-semibold"
        style={(node.breakdownBy === o.dim && node.mode === 'groups')
          ? { background: T.primary, color: '#fff' }
          : { color: o.splits ? T.body : T.muted,
              border: `1px solid ${T.border}`, opacity: o.splits ? 1 : 0.45 }}>
        {o.label}
        <span className="ml-1 text-[9px] font-normal">{int(o.distinct)}</span>
      </button>
    ))}
    <button type="button" onClick={() => { setBy('invoice'); }} data-testid="an-by-invoice"
      className="px-2 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1"
      style={node.mode === 'invoices'
        ? { background: T.primary, color: '#fff' }
        : { color: T.body, border: `1px solid ${T.border}` }}>
      <Receipt className="w-3 h-3" /> The invoices
    </button>
    {!(node.breakdownOptions || []).some((o) => o.splits) && (
      <span className="text-[10px]" style={{ color: T.muted }} data-testid="an-exhausted">
        every dimension is now a single value — the invoices are the next level
      </span>
    )}
  </div>
);

/* ── the grouped table ────────────────────────────────────────────────── */
const GroupTable = ({ node, cur, T, onDrill }) => (
  <Panel T={T} testId="an-groups" title={`L${node.depth + 2} — by ${node.dimLabel?.toLowerCase()}`}
    subtitle={`${int((node.rows || []).length)} rows · click one to go a level deeper`}
    right={<Def T={T} testId="an-groups-def" title={node.dimLabel} body={node.dimNote}
      sample={node.unkeyed?.note} />}>
    {node.doubleCount && (
      <div className="mb-2 rounded-md p-2 text-[10.5px] leading-relaxed"
        data-testid="an-double-count"
        style={{ background: T.tint(T.amber, '14'), color: T.ink,
                 border: `1px solid ${T.tint(T.amber, '44')}` }}>
        <span className="font-bold uppercase tracking-wide text-[9.5px]"
          style={{ color: T.amber }}>double counted on purpose · </span>
        {node.doubleCount.statement}
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="an-groups-table">
        <thead>
          <tr style={{ color: T.muted, background: T.surfaceAlt }}>
            {[node.dimLabel, 'Inv', 'Gross', 'Paid %', 'FPY %', 'STP %', 'On-time %',
              'Net cycle', 'Dispute %', 'Vendor d', 'Client d', 'Unknown d', 'Blocked',
              'Ded %', 'Imported'].map((h, i) => (
                <th key={h} className={`py-2 px-2 font-semibold whitespace-nowrap ${i ? 'text-right' : 'text-left'}`}>
                  {h}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {(node.rows || []).map((r) => (
            <tr key={r.key} data-testid={`an-group-row-${r.key}`}
              onClick={() => onDrill(node.dim, r.key)} className="cursor-pointer"
              style={{ borderTop: `1px solid ${T.border}` }}>
              <td className="py-1.5 px-2 font-semibold whitespace-nowrap"
                style={{ color: T.primary }}>
                {node.dim === 'stage' ? <StageChip stage={r.key} T={T} /> : r.label}
              </td>
              <Num v={int(r.invoices)} T={T} />
              <Num v={money(r.grossValue, cur)} T={T} bold />
              <Num v={r.paidPct === null ? '—' : `${num(r.paidPct, 0)}%`} T={T} />
              <Num v={r.firstPassYield === null ? '—' : `${num(r.firstPassYield, 0)}%`} T={T}
                tone={r.firstPassYield < 70 ? T.red : undefined} />
              <Num v={r.straightThrough === null ? '—' : `${num(r.straightThrough, 0)}%`} T={T} />
              <Num v={r.onTimePayment === null ? '—' : `${num(r.onTimePayment, 0)}%`} T={T}
                tone={r.onTimePayment !== null && r.onTimePayment < 80 ? T.red : undefined} />
              <Num v={r.netCycleDays === null ? '—' : `${num(r.netCycleDays, 1)} d`} T={T} />
              <Num v={r.disputeRate === null ? '—' : `${num(r.disputeRate, 0)}%`} T={T}
                tone={r.disputeRate > 20 ? T.red : undefined} />
              <Num v={num(r.vendorDays, 1)} T={T} tone={toneColor(T, PARTY.VENDOR.tone)} />
              <Num v={num(r.clientDays, 1)} T={T} tone={toneColor(T, PARTY.CLIENT.tone)} />
              <Num v={num(r.neitherDays, 1)} T={T} tone={T.muted} />
              <Num v={r.blockedValue ? money(r.blockedValue, cur) : '—'} T={T}
                tone={r.blockedValue ? T.amber : undefined} />
              <Num v={r.deductionPctOfGross === null ? '—'
                : `${num(r.deductionPctOfGross, 1)}%`} T={T} />
              <Num v={r.imported ? `${int(r.imported)} (${num(r.pctImported, 0)}%)` : '—'}
                T={T} tone={r.imported ? T.amber : T.muted} />
            </tr>
          ))}
          {!(node.rows || []).length && (
            <tr><td colSpan={15}><Empty T={T}>No data for this dimension.</Empty></td></tr>
          )}
        </tbody>
      </table>
    </div>
    {node.unkeyed && (
      <p className="mt-2 text-[10px]" data-testid="an-unkeyed"
        style={{ color: T.muted }}>{node.unkeyed.note}</p>
    )}
    <Sample T={T} testId="an-groups-sample"
      text={'Vendor / client / unknown days are the attribution axis — a record of which '
        + 'party the invoice was waiting on. They are independent of whether the contract '
        + 'suspended the clock, and the unknown column is never folded into either party.'} />
  </Panel>
);

/* ── the invoice list ─────────────────────────────────────────────────── */
const InvoiceTable = ({ node, cur, T, onOpen, onFull }) => (
  <Panel T={T} testId="an-invoices" title={`L${node.depth + 2} — the invoices`}
    subtitle={`${int(node.returned)} of ${int(node.total)} shown · ${node.sortNote || ''} `
      + '· click one for its transitions'}
    right={<Def T={T} testId="an-invoices-def" title="The invoice level"
      body={node.nextLevel}
      sample="Opening an invoice here shows its stage transitions in place; opening a
        transition shows the single audited fact behind it. The full invoice record is one
        click further." />}>
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="an-invoices-table">
        <thead>
          <tr style={{ color: T.muted, background: T.surfaceAlt }}>
            {['Invoice', 'Vendor', 'Stage', 'Waiting on', 'Received', 'Due', 'Ageing',
              'Beyond', 'Moves', 'Net payable', ''].map((h, i) => (
                <th key={h || i} className={`py-2 px-2 font-semibold whitespace-nowrap ${i > 3 ? 'text-right' : 'text-left'}`}>
                  {h}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {(node.invoices || []).map((r) => (
            <tr key={r.invoice_id} data-testid={`an-invoice-row-${r.invoice_no}`}
              onClick={() => onOpen(r.invoice_id)} className="cursor-pointer"
              style={{ borderTop: `1px solid ${T.border}` }}>
              <td className="py-1.5 px-2 font-semibold" style={{ color: T.primary }}>
                {r.invoice_no}
                {r.imported_without_history && (
                  <span className="ml-1 px-1 rounded text-[8px] font-bold uppercase"
                    style={{ background: T.tint(T.amber, '1A'), color: T.amber }}>
                    imported
                  </span>
                )}
              </td>
              <td className="py-1.5 px-2 truncate max-w-[150px]" style={{ color: T.ink }}>
                {r.vendor_name}
              </td>
              <td className="py-1.5 px-2"><StageChip stage={r.current_stage} T={T} /></td>
              <td className="py-1.5 px-2">
                {r.waiting_on ? <WaitingPill party={r.waiting_on} T={T} /> : '—'}
              </td>
              <Num v={day(r.receipt_date)} T={T} />
              <Num v={day(r.due_date)} T={T} />
              <Num v={num(r.metrics.ageing_days, 0)} T={T} />
              <Num v={r.metrics.days_beyond_terms > 0
                ? num(r.metrics.days_beyond_terms, 0) : '—'} T={T}
                tone={r.metrics.days_beyond_terms > 0 ? T.red : undefined} />
              <Num v={int(r.transitionCount)} T={T} />
              <Num v={money(r.net_payable, r.currency || cur)} T={T} bold />
              <td className="py-1.5 px-2 text-right">
                <button type="button" data-testid={`an-invoice-full-${r.invoice_no}`}
                  onClick={(e) => { e.stopPropagation(); onFull(r.invoice_id); }}
                  className="text-[9.5px] underline" style={{ color: T.muted }}>
                  full record
                </button>
              </td>
            </tr>
          ))}
          {!(node.invoices || []).length && (
            <tr><td colSpan={11}><Empty T={T}>No invoices in this node.</Empty></td></tr>
          )}
        </tbody>
      </table>
    </div>
  </Panel>
);

/* ── one invoice: its transitions ─────────────────────────────────────── */
const InvoicePanel = ({ d, T, cur, onOpenTransition, onClose, onFull, projectId, basis }) => {
  if (d.loading) {
    return (
      <Panel T={T} testId="an-invoice-node" title="Loading the transition history…">
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: T.primary }} />
      </Panel>
    );
  }
  const inv = d.invoice;
  return (
    <>
      <Panel T={T} testId="an-invoice-node"
        title={`Invoice level — ${inv.invoice_no}`}
        subtitle={`${inv.vendor_name} · ${int(d.transitions.length)} transitions · `
          + `${money(inv.net_payable, inv.currency || cur)} net payable`}
        right={(
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => onFull(inv.invoice_id)}
              data-testid="an-invoice-node-full" className="text-[10px] underline"
              style={{ color: T.muted }}>full record</button>
            <button type="button" onClick={onClose} data-testid="an-invoice-node-close"
              className="text-[10px]" style={{ color: T.muted }}>close</button>
          </div>
        )}>
        <ol className="space-y-0" data-testid="an-transition-list">
          {d.transitions.map((t, i) => (
            <li key={t.transition_id || i}>
              <button type="button" onClick={() => onOpenTransition(t.transition_id)}
                data-testid={`an-transition-${i}`}
                className="w-full text-left py-1.5 flex items-baseline gap-2 text-[11px] group"
                style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <span className="w-5 text-[9.5px] tabular-nums" style={{ color: T.muted }}>
                  {t.seq}
                </span>
                <span className="w-[108px] shrink-0 text-[10px] tabular-nums"
                  style={{ color: T.muted }}>{stamp(t.occurred_on)}</span>
                <span style={{ color: T.body }}>
                  {t.from_stage ? `${t.from_label} → ` : 'entered '}
                  <span className="font-semibold" style={{ color: T.ink }}>{t.to_label}</span>
                </span>
                {t.dwellDays !== null && t.dwellDays !== undefined && (
                  <span className="text-[10px] tabular-nums" style={{ color: T.body }}>
                    {num(t.dwellDays, 1)} d in {STAGE_LABEL[t.spanOfStage] || t.spanOfStage}
                  </span>
                )}
                {t.suspends && (
                  <span className="px-1.5 rounded text-[9px] font-bold uppercase"
                    style={{ background: T.tint(T.amber, '1F'), color: T.amber }}>
                    clock stopped
                  </span>
                )}
                {t.waiting_on && <WaitingPill party={t.waiting_on}
                  inferred={t.waiting_on_source === 'inferred'} T={T} />}
                <span className="ml-auto text-[9.5px]" style={{ color: T.muted }}>
                  {t.actor_id} · {t.source}
                </span>
                <CornerDownRight className="w-3 h-3 opacity-0 group-hover:opacity-100"
                  style={{ color: T.primary }} />
              </button>
            </li>
          ))}
          {!d.transitions.length && <Empty T={T}>No transitions recorded.</Empty>}
        </ol>
        <Sample T={T} testId="an-invoice-node-sample"
          text={`${d.axes.suspension.note} ${d.axes.attribution.note} ${d.payable.statement}`} />
      </Panel>
      <CognaraPanel level="INVOICE" projectId={projectId} monthBasis={basis}
        invoiceId={inv.invoice_id}
        subtitle={`${inv.invoice_no} — the full transition history`} />
    </>
  );
};

/* ── one transition: the audited fact ─────────────────────────────────── */
const TransitionPanel = ({ d, T, onClose, onStep, projectId, basis }) => {
  if (d.loading) {
    return (
      <Panel T={T} testId="an-transition-node" title="Loading the transition…">
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: T.primary }} />
      </Panel>
    );
  }
  const t = d.transition;
  return (
    <>
      <Panel T={T} testId="an-transition-node"
        title={`Transition level — ${t.from_label || 'entry'} → ${t.to_label}`}
        subtitle={`${d.invoice.invoice_no} · move ${int(d.position.index)} of `
          + `${int(d.position.of)} · ${stamp(t.occurred_on)}`}
        right={(
          <div className="flex items-center gap-2">
            {d.previous && (
              <button type="button" onClick={() => onStep(d.previous.transition_id)}
                data-testid="an-transition-prev" className="text-[10px] underline"
                style={{ color: T.muted }}>previous move</button>
            )}
            {d.next && (
              <button type="button" onClick={() => onStep(d.next.transition_id)}
                data-testid="an-transition-next" className="text-[10px] underline"
                style={{ color: T.muted }}>next move</button>
            )}
            <button type="button" onClick={onClose} data-testid="an-transition-close"
              className="text-[10px]" style={{ color: T.muted }}>close</button>
          </div>
        )}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-md p-2.5" data-testid="an-transition-suspension"
            style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
            <div className="text-[9.5px] uppercase tracking-[0.09em] font-bold"
              style={{ color: T.muted }}>
              Suspension · {d.axes.suspension.rule}
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: T.ink }}>
              {d.axes.suspension.entryRow ? 'entry row'
                : (d.axes.suspension.suspends ? 'clock stopped' : 'clock running')}
              {t.dwellDays !== null && t.dwellDays !== undefined
                && ` · ${num(t.dwellDays, 2)} d`}
            </div>
            <p className="text-[10.5px] mt-1 leading-relaxed" style={{ color: T.body }}>
              {d.axes.suspension.statement}
            </p>
          </div>
          <div className="rounded-md p-2.5" data-testid="an-transition-attribution"
            style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
            <div className="text-[9.5px] uppercase tracking-[0.09em] font-bold"
              style={{ color: T.muted }}>
              Attribution · {d.axes.attribution.rule}
            </div>
            <div className="text-[11.5px] mt-1 flex items-center gap-2">
              {t.waiting_on ? <WaitingPill party={t.waiting_on}
                inferred={t.waiting_on_source === 'inferred'} T={T} />
                : <span style={{ color: T.muted }}>not recorded</span>}
              <span className="text-[10px]" style={{ color: T.muted }}>
                {d.axes.attribution.source || '—'}
              </span>
            </div>
            <p className="text-[10.5px] mt-1 leading-relaxed" style={{ color: T.body }}>
              {d.axes.attribution.statement}
            </p>
            {d.axes.attribution.dispute_basis && (
              <p className="text-[10.5px] mt-1" style={{ color: T.ink }}>
                Basis: {d.axes.attribution.dispute_basis}
              </p>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-3 text-[10.5px]">
          {[['Actor', `${t.actor_id} · ${t.actor_role || '—'}`],
            ['Actor type', t.actor_type || '—'],
            ['Source', t.source || '—'],
            ['Correlation id', t.correlation_id || '—'],
            ['Reason', t.reason || '—'],
            ['Recorded at', stamp(t.created_at || t.occurred_on)]].map(([kk, vv]) => (
              <div key={kk}>
                <dt className="text-[9px] uppercase tracking-[0.08em]" style={{ color: T.muted }}>
                  {kk}
                </dt>
                <dd className="break-all" style={{ color: T.ink }}>{vv}</dd>
              </div>
            ))}
        </dl>
        <Sample T={T} testId="an-transition-sample"
          text={`${d.axes.note} ${d.auditNote}`} />
        {!!(d.audit || []).length && (
          <ol className="mt-2 pt-2 space-y-0.5" data-testid="an-transition-audit"
            style={{ borderTop: `1px dashed ${T.border}` }}>
            {d.audit.map((e, i) => (
              <li key={e.eventId || i} className="text-[10px] flex gap-2">
                <span className="tabular-nums" style={{ color: T.muted }}>#{e.seq}</span>
                <span className="font-semibold" style={{ color: T.ink }}>{e.action}</span>
                <span style={{ color: T.muted }}>{stamp(e.timestamp)} · {e.actor_id}</span>
              </li>
            ))}
          </ol>
        )}
      </Panel>
      <CognaraPanel level="TRANSITION" projectId={projectId} monthBasis={basis}
        transitionId={t.transition_id}
        subtitle={`${d.invoice.invoice_no} · ${t.from_label || 'entry'} → ${t.to_label}`} />
    </>
  );
};

/* ── charts, every element a drill target ─────────────────────────────── */
const Charts = ({ node, k, cur, T, basis, onDrill }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
    <Panel T={T} testId="an-chart-trend" title="Intake and payment by month"
      subtitle={basis === 'status' ? 'By month the status was reached' : 'By receipt month'}
      right={<Def T={T} testId="an-trend-def" title="Monthly trend"
        body={(node.monthBasisOptions || []).find((o) => o.value === basis)?.note}
        basis={k.basis} sample="Click a month to drill into it." />}>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={80} debounce={60}>
          <AreaChart data={node.charts.trend}
            onClick={(e) => onDrill('month', e?.activeLabel)}>
            <defs>
              <linearGradient id="anRecv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.primary} stopOpacity={0.4} />
                <stop offset="100%" stopColor={T.primary} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={T.border} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.muted }} />
            <YAxis tick={{ fontSize: 9, fill: T.muted }} width={34} />
            <RTooltip contentStyle={{ background: T.surface, fontSize: 11,
              border: `1px solid ${T.border}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Area type="monotone" dataKey="received" name="received" stroke={T.primary}
              strokeWidth={2} fill="url(#anRecv)" />
            <Line type="monotone" dataKey="paid" name="paid" stroke={T.green}
              strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="disputed" name="disputed" stroke={T.red}
              strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="imported" name="imported" stroke={T.amber}
              strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>

    <Panel T={T} testId="an-chart-dwell" title="Stage dwell — native vs all-in"
      subtitle="Where the time goes, with the imported band separated"
      right={<Def T={T} testId="an-dwell-def" title="Stage dwell"
        body={k.bottleneck.definition} basis={k.basis}
        sample="Click a stage to drill into the invoices sitting there." />}>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={80} debounce={60}>
          <BarChart data={node.charts.stageDwell} layout="vertical"
            onClick={(e) => onDrill('stage', e?.activePayload?.[0]?.payload?.stage)}>
            <CartesianGrid stroke={T.border} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 9, fill: T.muted }} />
            <YAxis type="category" dataKey="label" width={104}
              tick={{ fontSize: 9, fill: T.muted }} />
            <RTooltip contentStyle={{ background: T.surface, fontSize: 11,
              border: `1px solid ${T.border}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <RBar dataKey="avgDaysExclImported" name="native only" fill={T.primary}
              cursor="pointer" />
            <RBar dataKey="avgDays" name="including imported" fill={T.amber}
              fillOpacity={0.45} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>

    <Panel T={T} testId="an-chart-ageing" title="Ageing of unpaid invoices"
      subtitle={`${int(node.charts.ageingTotal?.count)} unpaid · `
        + `${money(node.charts.ageingTotal?.value, cur)}`}
      right={<Def T={T} testId="an-ageing-def" title="Ageing buckets"
        body={(node.definitions?.ageing || {}).basis} basis={k.basis}
        sample={node.charts.ageingTotal?.note} />}>
      <div className="space-y-2">
        {(node.charts.ageing || []).map((b, i) => {
          const c = toneColor(T, ['primary', 'amber', 'accent', 'red'][i] || 'muted');
          const maxV = Math.max(...(node.charts.ageing || []).map((x) => x.value || 0), 1);
          return (
            <button key={b.bucket} type="button" data-testid={`an-ageing-${b.bucket}`}
              onClick={() => onDrill('ageing', b.bucket)} className="w-full text-left group">
              <div className="flex items-baseline gap-2 text-[11px]">
                <span className="w-14 font-semibold" style={{ color: T.ink }}>{b.bucket} d</span>
                <span className="tabular-nums" style={{ color: T.body }}>{int(b.count)}</span>
                {b.imported > 0 && (
                  <span className="text-[9px]" style={{ color: T.amber }}>
                    {int(b.imported)} imported
                  </span>
                )}
                <span className="ml-auto tabular-nums font-semibold" style={{ color: c }}>
                  {money(b.value, cur)}
                </span>
              </div>
              <Bar T={T} height={8} segments={[
                { label: 'native', value: b.value * (1 - b.imported / (b.count || 1)), colour: c },
                { label: 'imported', value: b.value * (b.imported / (b.count || 1)),
                  colour: c, hatch: true },
              ]} />
            </button>
          );
        })}
        {!(node.charts.ageing || []).length && <Empty T={T}>No unpaid invoices here.</Empty>}
      </div>
    </Panel>

    <Panel T={T} testId="an-chart-attribution" title="Delay attribution"
      subtitle="Who the invoices were waiting on"
      right={<Def T={T} testId="an-attr-def" title="Delay attribution"
        body={k.attribution.definition} basis={k.basis}
        sample={`${int(k.attribution.spans)} periods; ${int(k.attribution.inferredSpans)} `
          + `(${num(k.attribution.pctInferred, 1)}%) have no recorded party and are shown `
          + 'as unknown. Click a party to drill into it.'}
        axis={k.attribution.axis} version={k.attribution.version} />}>
      <div className="h-44 flex items-center">
        <ResponsiveContainer width="55%" height="100%" minWidth={0} minHeight={80} debounce={60}>
          <PieChart>
            <Pie data={node.charts.attribution} dataKey="days" nameKey="party"
              innerRadius={34} outerRadius={62} paddingAngle={2}
              onClick={(e) => onDrill('waiting_on', e?.payload?.party || e?.party)}
              cursor="pointer">
              {node.charts.attribution.map((e) => (
                <Cell key={e.party} fill={toneColor(T, PARTY[e.party].tone)} />
              ))}
            </Pie>
            <RTooltip formatter={(v) => `${num(v)} d`}
              contentStyle={{ background: T.surface, fontSize: 11,
                border: `1px solid ${T.border}`, borderRadius: 6 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1.5">
          {node.charts.attribution.map((e) => (
            <button key={e.party} type="button" data-testid={`an-attr-${e.party}`}
              onClick={() => onDrill('waiting_on', e.party)}
              className="w-full flex items-center gap-2 text-[11px]">
              <span className="w-2.5 h-2.5 rounded-sm"
                style={{ background: toneColor(T, PARTY[e.party].tone) }} />
              <span style={{ color: T.body }}>{PARTY[e.party].label}</span>
              <span className="ml-auto tabular-nums font-semibold" style={{ color: T.ink }}>
                {num(e.days)} d
              </span>
            </button>
          ))}
          {k.attribution.inferredSpans > 0 && (
            <div className="text-[10px] pt-1.5" data-testid="an-attr-unknown"
              style={{ color: T.muted, borderTop: `1px dashed ${T.border}` }}>
              {int(k.attribution.inferredSpans)} of {int(k.attribution.spans)} periods
              ({num(k.attribution.pctInferred, 1)}%) carry no recorded party — unknown,
              not assigned.
            </div>
          )}
        </div>
      </div>
    </Panel>
  </div>
);

const Num = ({ v, T, tone, bold }) => (
  <td className={`py-1.5 px-2 text-right tabular-nums whitespace-nowrap ${bold ? 'font-semibold' : ''}`}
    style={{ color: tone || T.body }}>{v}</td>
);

export default IptExplorer;
